<script setup lang="ts">
import { useEventPayoutInfoQuery, useTriggerEventPayoutMutation } from '@/enitites/event/api/queries'
import { mdiChevronDown, mdiChevronUp } from '@mdi/js'
import { ref } from 'vue'

const props = defineProps<{
  eventId: number
}>()

const { data: payoutInfo } = useEventPayoutInfoQuery(props.eventId)
const { mutate, isPending } = useTriggerEventPayoutMutation()

const isExpanded = ref(false)

const formattedPayoutDate = (date: string) => {
  const d = new Date(date)
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  const yy = String(d.getFullYear()).slice(-2)
  return `${mm}.${dd}.${yy}`
}

const triggerPayout = () => {
  mutate(props.eventId)
}
</script>

<template>
  <div
    v-if="payoutInfo && payoutInfo.payoutDate"
    class="event-payout-trigger"
  >
    <button
      class="payout-header"
      @click="isExpanded = !isExpanded"
    >
      <span class="payout-label">{{ isExpanded ? 'Payout' : 'Payment arrival' }}</span>
      <span class="payout-right">
        <span class="payout-date">{{ formattedPayoutDate(payoutInfo.payoutDate) }}</span>
        <v-icon
          size="20"
          color="#DDDDDD"
          >{{ isExpanded ? mdiChevronUp : mdiChevronDown }}</v-icon
        >
      </span>
    </button>

    <div
      v-if="isExpanded"
      class="payout-body"
    >
      <template v-if="payoutInfo.status === 'not_eligible'">
        Your event is not eligible for payout at this time.
      </template>
      <template v-else-if="payoutInfo.status === 'waiting'">
        You will receive a payout within 2 business days after the event is over, on
        {{
          new Date(payoutInfo.payoutDate).toLocaleDateString('en-US', {
            month: 'long',
            day: 'numeric',
            year: 'numeric',
          })
        }}.
      </template>
      <template v-else-if="payoutInfo.status === 'pending'">
        <BKButton
          :disabled="!payoutInfo.canDoPayout"
          :loading="isPending"
          white-text
          @click="triggerPayout"
        >
          Trigger Payout
        </BKButton>
      </template>
      <template v-else-if="payoutInfo.status === 'processing'">
        Payout is currently being processed. Please wait.
      </template>
      <template v-else-if="payoutInfo.status === 'paid'"> Payout has been completed successfully. </template>
      <template v-else-if="payoutInfo.status === 'failed'">
        <p>Payout failed. Please contact support for assistance.</p>
        <BKButton
          :disabled="!payoutInfo.canDoPayout"
          :loading="isPending"
          white-text
          @click="triggerPayout"
        >
          Retry Payout
        </BKButton>
      </template>
    </div>
  </div>
</template>

<style scoped lang="scss">
.event-payout-trigger {
  display: flex;
  flex-direction: column;
  color: white;
}

.payout-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 10px 0 0;
  background: none;
  border: none;
  color: #ddd;
  cursor: pointer;
  font-size: 14px;
}

.payout-label {
  font-size: 14px;
}

.payout-right {
  display: flex;
  align-items: center;
  gap: 10px;
}

.payout-date {
  font-size: 14px;
}

.payout-body {
  margin-top: 10px;
  font-size: 16px;
  line-height: 1.3;
  color: #eee;
}
</style>
